import { normalizeStatusToken, normalizeToken, type AvailabilityProfile } from '@ooh/contracts';

export interface UnmappedStatusToken {
  /** Normalized (trimmed/lowercased/whitespace-collapsed) token — the exact
   * key `normalizeStatusToken` looks up in `profile.statusDictionary`, so
   * this is also the key the editor writes back to when the operator maps
   * this row. */
  rawToken: string;
  /** How many cells across the file's status-bearing column(s) (within the
   * loaded sample) carry this exact normalized token. */
  occurrenceCount: number;
}

/** The column(s) whose cell values feed `normalizeStatusToken` for the
 * current profile — one column per detected period for month/half_month
 * (one status token per period per row), else the single `statusColumn`
 * when set (date_range/global/period_label_map profiles that have one). */
function statusBearingColumns(profile: AvailabilityProfile): string[] {
  if (profile.timeEncoding === 'month' || profile.timeEncoding === 'half_month') {
    return profile.periodColumns.map((pc) => pc.column);
  }
  return profile.statusColumn ? [profile.statusColumn] : [];
}

/**
 * Distinct raw status tokens found in the loaded sample rows that
 * `normalizeStatusToken` currently reports as unmapped (CU-86cb1npz2, gap
 * #3) — not recognized by the canonical FREE/BUSY vocabulary, not covered
 * by `profile.statusDictionary`, and (for a `list_of_free` file) not caught
 * by the busy-catch-all. As the operator maps a token via
 * `StatusTokenMappingTable` (writing into `profile.statusDictionary`),
 * `normalizeStatusToken` resolves it on the next render and it naturally
 * drops out of this list — the table shows exactly what still needs
 * attention, not a static one-time snapshot.
 *
 * Sourced from the already-loaded `sampleRows` (capped at
 * `SAMPLE_ROWS_LIMIT` server-side) rather than a new backend endpoint — see
 * PLAN.md's Task 2 notes: `occurrenceCount` therefore reflects the sample
 * window, not the whole file, matching the same known limitation already
 * accepted for `periodLabelMappings`' distinct-value derivation elsewhere in
 * this hook family.
 */
export function deriveUnmappedStatusTokens(
  profile: AvailabilityProfile,
  sampleRows: Record<string, string>[],
): UnmappedStatusToken[] {
  const columns = statusBearingColumns(profile);
  if (columns.length === 0) return [];

  const counts = new Map<string, number>();
  for (const row of sampleRows) {
    for (const column of columns) {
      const raw = row[column];
      if (!raw) continue;
      const result = normalizeStatusToken(raw, profile);
      if (!('unmapped' in result)) continue;
      const key = normalizeToken(raw);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  return Array.from(counts.entries()).map(([rawToken, occurrenceCount]) => ({
    rawToken,
    occurrenceCount,
  }));
}
